package kz.dentalcrm.app.screens.employees

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.Plus
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.doctors
import kz.dentalcrm.app.core.ui.components.ContentButton
import kz.dentalcrm.app.core.ui.components.lazy_column.PaginatedLazyColumn
import kz.dentalcrm.app.core.ui.components.refresh.PullToRefresh
import kz.dentalcrm.app.core.ui.components.refresh.rememberPullToRefreshState
import kz.dentalcrm.app.data.network.response_model.employees.Employee
import kz.dentalcrm.app.navigation.AppNavigator
import kz.dentalcrm.app.navigation.NavigationResults
import kz.dentalcrm.app.navigation.AddEmployeeRoute
import kz.dentalcrm.app.navigation.EditEmployeeRoute
import kz.dentalcrm.app.navigation.resultFlow
import kz.dentalcrm.app.screens.employees.mvi.EmployeesState
import kz.dentalcrm.app.screens.employees.mvi.EmployeesViewEvent
import kz.dentalcrm.app.screens.ui.ContentHeader
import kz.dentalcrm.app.screens.ui.Icons.chevronRight
import kz.dentalcrm.app.theme.DefaultTheme
import kz.dentalcrm.app.theme.shimmer.ShimmerBounds
import kz.dentalcrm.app.theme.shimmer.rememberShimmer
import kz.dentalcrm.app.theme.shimmer.shimmer
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun EmployeesScreen() {
    val navigator: AppNavigator = koinInject()
    val results: NavigationResults = koinInject()
    val viewModel: EmployeesViewModel = koinViewModel()

    LaunchedEffect(results) {
        results.resultFlow<AddEmployeeRoute.Result>(AddEmployeeRoute.RESULT_KEY)
            .collect {
                viewModel.setEvent(EmployeesViewEvent.Refresh)
                results.clear(AddEmployeeRoute.RESULT_KEY)
            }
    }

    LaunchedEffect(results) {
        results.resultFlow<EditEmployeeRoute.Result>(EditEmployeeRoute.RESULT_KEY)
            .collect { result ->
                if (result.updated || result.deleted) {
                    viewModel.setEvent(EmployeesViewEvent.Refresh)
                }
                results.clear(EditEmployeeRoute.RESULT_KEY)
            }
    }

    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        val viewState by viewModel.viewState.collectAsState()
        EmployeesContent(
            state = viewState,
            event = viewModel::setEvent,
            navigator = navigator
        )
    }
}

@Composable
fun EmployeesContent(
    state: EmployeesState,
    event: (EmployeesViewEvent) -> Unit = {},
    navigator: AppNavigator
) {
    val isLoading = state.screenState is EmployeesState.ScreenState.Loading
    var isRefreshing by remember { mutableStateOf(false) }
    val pullToRefreshState = rememberPullToRefreshState(isRefreshing = isRefreshing)
    LaunchedEffect(isLoading) { if (!isLoading) isRefreshing = false }

    Box(
        modifier = Modifier.fillMaxSize()
            .safeDrawingPadding()
            .padding(horizontal = 12.dp)
    ) {
        Column(
            modifier = Modifier.fillMaxSize()
        ) {
            ContentHeader(
                modifier = Modifier.fillMaxWidth(),
                text = stringResource(Res.string.doctors)
            )
            PullToRefresh(
                state = pullToRefreshState,
                onRefresh = {
                    isRefreshing = true
                    event(EmployeesViewEvent.Refresh)
                },
                modifier = Modifier.fillMaxSize()
            ) {
                Body(
                    state,
                    event = event,
                    isLoading = isLoading,
                    onEmployeeClick = { employeeId ->
                        navigator.navigate(EditEmployeeRoute(employeeId))
                    }
                )
            }
        }
        FloatingActionButton(
            modifier = Modifier
                .navigationBarsPadding()
                .padding(
                    bottom = 96.dp,
                    start = 12.dp,
                    end = 12.dp,
                    top = 16.dp,
                ).align(Alignment.BottomEnd),
            shape = RoundedCornerShape(50),
            containerColor = DefaultTheme.colors.primary,
            contentColor = DefaultTheme.colors.onPrimary,
            onClick = {
                navigator.navigate(AddEmployeeRoute)
            }
        ) {
            Icon(
                imageVector = FeatherIcons.Plus,
                contentDescription = null
            )
        }
    }
}

@Composable
private fun Body(
    state: EmployeesState,
    event: (EmployeesViewEvent) -> Unit = {},
    isLoading: Boolean,
    onEmployeeClick: (Int) -> Unit
) {
    val shimmer = rememberShimmer(ShimmerBounds.Window)
    val listState = rememberLazyListState()

    if (isLoading && state.employees.isEmpty()) {
        Column(modifier = Modifier.widthIn().padding(top = 16.dp)) {
            repeat(8) { EmployeeItemSkeleton(shimmer = shimmer) }
            Spacer(modifier = Modifier.height(220.dp))
        }
    } else {
        val listModifier = if (isLoading) Modifier.widthIn().shimmer(shimmer) else Modifier.widthIn()
        PaginatedLazyColumn(
            modifier = listModifier,
            loadMoreItems = { event(EmployeesViewEvent.LoadMoreEmployees) },
            listState = listState,
            buffer = 5,
            isLoading = isLoading,
            paginatedTextError = (state.screenState as? EmployeesState.ScreenState.Error)?.text.orEmpty(),
            retryOnPaginated = { event(EmployeesViewEvent.LoadMoreEmployees) }
        ) {
            item {
                Spacer(modifier = Modifier.height(16.dp))
            }
            itemsIndexed(
                items = state.employees
            ) { _, employee ->
                EmployeeItem(item = employee) {
                    onEmployeeClick(employee.id)
                }
            }
            item {
                Spacer(modifier = Modifier.height(220.dp))
            }
        }
    }
}

@Composable
private fun EmployeeItemSkeleton(
    shimmer: kz.dentalcrm.app.theme.shimmer.Shimmer
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                color = DefaultTheme.colors.primarySubdued,
                shape = RoundedCornerShape(16.dp)
            ),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Spacer(modifier = Modifier.width(12.dp))
        // Avatar circle placeholder
        Box(
            modifier = Modifier
                .size(40.dp)
                .shimmer(shimmer)
                .background(
                    color = DefaultTheme.colors.onSurface.copy(alpha = 0.12f),
                    shape = RoundedCornerShape(40.dp)
                )
        )
        Spacer(modifier = Modifier.width(16.dp))
        Column(modifier = Modifier.weight(1f)) {
            Spacer(modifier = Modifier.height(12.dp))
            Box(
                modifier = Modifier
                    .fillMaxWidth(0.6f)
                    .height(14.dp)
                    .shimmer(shimmer)
                    .background(
                        color = DefaultTheme.colors.onSurface.copy(alpha = 0.12f),
                        shape = RoundedCornerShape(4.dp)
                    )
            )
            Spacer(modifier = Modifier.height(4.dp))
            Box(
                modifier = Modifier
                    .fillMaxWidth(0.4f)
                    .height(12.dp)
                    .shimmer(shimmer)
                    .background(
                        color = DefaultTheme.colors.onSurface.copy(alpha = 0.12f),
                        shape = RoundedCornerShape(4.dp)
                    )
            )
            Spacer(modifier = Modifier.height(12.dp))
        }
        Spacer(modifier = Modifier.width(32.dp))
    }
    Spacer(Modifier.height(12.dp))
}

@Composable
fun EmployeeItem(
    item: Employee,
    onClick: () -> Unit,
) {
    ContentButton(
        modifier = Modifier,
        shape = RoundedCornerShape(16.dp),
        onClick = onClick
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn()
                .background(
                    color = DefaultTheme.colors.primarySubdued,
                    shape = RoundedCornerShape(16.dp)
                ),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Spacer(modifier = Modifier.width(12.dp))
            Box(
                modifier = Modifier.size(40.dp)
                    .background(
                        color = DefaultTheme.colors.primary,
                        shape = RoundedCornerShape(40.dp)
                    )
            ) {
                Text(
                    text = item.surname?.first().toString().uppercase() +
                            item.name.orEmpty().first().toString().uppercase(),
                    color = DefaultTheme.colors.onPrimary,
                    style = DefaultTheme.typography.bodyMediumMedium,
                    modifier = Modifier.align(Alignment.Center)
                )
            }
            Spacer(modifier = Modifier.width(16.dp))
            Column(
                modifier = Modifier.weight(1f)
            ) {
                Spacer(modifier = Modifier.height(12.dp))
                Text(
                    text = "${item.surname} ${item.name}",
                    color = DefaultTheme.colors.onSurface,
                    overflow = TextOverflow.Ellipsis,
                    maxLines = 1,
                    style = DefaultTheme.typography.bodyMediumMedium,
                )
                Spacer(modifier = Modifier.height(2.dp))
                Text(
                    text = if ((item.phone?.first()
                            ?: "") != "+"
                    ) "+${item.phone}" else "${item.phone}",
                    color = DefaultTheme.colors.onSurface,
                    style = DefaultTheme.typography.bodySmallRegular,
                )
                Spacer(modifier = Modifier.height(12.dp))
            }
            Spacer(modifier = Modifier.width(8.dp))
            Icon(
                imageVector = chevronRight,
                tint = DefaultTheme.colors.onSurface,
                contentDescription = null
            )
            Spacer(modifier = Modifier.width(8.dp))
        }
    }
    Spacer(Modifier.height(12.dp))
}
